/* Yard of Cards · editorial and consent-aware enhancements. */
const cfg = window.Yoc || {strings:{},root:'/',cart:'/cart'};
const options = cfg.editorial || {};
const strings = options.strings || {};
const viewKey = 'yoc-viewed-handles-v1';
const searchKey = 'yoc-searches-v1';
const preferences = () => window.Shopify?.customerPrivacy?.currentVisitorConsent?.().preferences === 'yes' && window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.() === true;
const marketing = () => window.Shopify?.customerPrivacy?.currentVisitorConsent?.().marketing === 'yes' && window.Shopify?.customerPrivacy?.marketingAllowed?.() === true;
const scrollBehavior = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
function read(key) {
  if (!preferences()) return [];
  try { const value = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(value) ? value.filter(x=>typeof x==='string').slice(0,8) : []; }
  catch { return []; }
}
function remember(key, value) {
  if (!preferences() || !value) return;
  try { localStorage.setItem(key,JSON.stringify([value,...read(key).filter(x=>x!==value)].slice(0,8))); }
  catch { /* Storage is optional. */ }
}
function clear(key) { try { localStorage.removeItem(key); } catch { /* Storage is optional. */ } }
const changed = () => document.dispatchEvent(new CustomEvent('yoc:history'));
function reconcileConsent() {
  if (!preferences()) { clear(viewKey); clear(searchKey); }
  else {
    if (options.recent && /^[a-z0-9-]{1,255}$/.test(options.product || '')) remember(viewKey, options.product);
    if (options.searches && typeof options.query === 'string') remember(searchKey,options.query.trim().slice(0,120));
  }
  changed();
}

class YocContentTabs extends HTMLElement {
  connectedCallback() {
    this.events = new AbortController();
    this.tabs = [...this.querySelectorAll('[data-content-tab]')];
    this.panels = [...this.querySelectorAll('[data-content-panel]')];
    if (!this.tabs.length || this.tabs.length !== this.panels.length) return;
    this.querySelector('[data-content-tablist]')?.setAttribute('role','tablist');
    this.tabs.forEach((tab,i)=> {
      tab.setAttribute('role','tab'); tab.setAttribute('aria-controls',this.panels[i].id);
      this.panels[i].setAttribute('role','tabpanel'); this.panels[i].setAttribute('aria-labelledby',tab.id);
      tab.addEventListener('click',event=> { if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return; event.preventDefault(); this.select(i); },{signal:this.events.signal});
      tab.addEventListener('keydown',event=> {
        const keys={ArrowRight:(i+1)%this.tabs.length,ArrowLeft:(i-1+this.tabs.length)%this.tabs.length,Home:0,End:this.tabs.length-1};
        if (!(event.key in keys)) return;
        event.preventDefault(); this.select(keys[event.key]); this.tabs[keys[event.key]].focus();
      },{signal:this.events.signal});
    });
    const hashIndex=this.panels.findIndex(panel=>`#${panel.id}`===location.hash);
    this.select(hashIndex<0 ? 0 : hashIndex);
  }
  select(index) {
    this.tabs.forEach((tab,i)=> { tab.setAttribute('aria-selected',String(i===index)); tab.tabIndex=i===index ? 0 : -1; this.panels[i].hidden=i!==index; });
  }
  disconnectedCallback() { this.events?.abort(); }
}

class YocArticle extends HTMLElement {
  connectedCallback() {
    const body=this.querySelector('[data-article-body]'), toc=this.querySelector('[data-article-toc]');
    if (!body || !toc) return;
    const headings=[...body.querySelectorAll('h2,h3')].filter(heading=>heading.textContent.trim());
    if (headings.length < 2) return;
    const list=toc.querySelector('ol'); list.replaceChildren();
    headings.forEach((heading,index)=> {
      if (!heading.id || document.getElementById(heading.id)!==heading) {
        let id=`yoc-heading-${index+1}`, suffix=1;
        while (document.getElementById(id)) id=`yoc-heading-${index+1}-${suffix++}`;
        heading.id=id;
      }
      const item=document.createElement('li'), link=document.createElement('a');
      link.href=`#${encodeURIComponent(heading.id)}`; link.textContent=heading.textContent;
      if (heading.tagName==='H3') item.className='yoc-toc-sub';
      item.append(link); list.append(item);
    });
    toc.hidden=false; toc.open=matchMedia('(min-width:1024px)').matches;
  }
}

class YocRecentSearches extends HTMLElement {
  connectedCallback() {
    this.events=new AbortController();
    document.addEventListener('yoc:history',()=>this.render(),{signal:this.events.signal});
    this.querySelector('[data-clear-search]')?.addEventListener('click',()=> { clear(searchKey); changed(); },{signal:this.events.signal});
    this.render();
  }
  render() {
    const items=options.searches ? read(searchKey).filter(x=>x.trim().length>0 && x.length<=120) : [];
    const list=this.querySelector('[data-search-history]'); if (!list) return;
    list.replaceChildren();
    for (const term of items) {
      const link=document.createElement('a'), url=new URL(options.searchURL || `${cfg.root}search`,location.origin);
      url.searchParams.set('q',term); link.href=url.href; link.textContent=term; list.append(link);
    }
    this.hidden=!items.length;
  }
  disconnectedCallback() { this.events?.abort(); }
}

class YocRecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.events=new AbortController();
    document.addEventListener('yoc:history',()=>this.render(),{signal:this.events.signal});
    this.querySelector('[data-clear-viewed]')?.addEventListener('click',()=> { clear(viewKey); changed(); },{signal:this.events.signal});
    this.querySelector('[data-recent-retry]')?.addEventListener('click',()=>this.render(),{signal:this.events.signal});
    this.render();
  }
  async render() {
    this.request?.abort(); this.request=new AbortController(); const signal=this.request.signal;
    const list=this.querySelector('[data-recent-items]'), status=this.querySelector('[data-recent-status]'), retry=this.querySelector('[data-recent-retry]');
    if (!list) return;
    const handles=options.recent ? read(viewKey).filter(x=>/^[a-z0-9-]{1,255}$/.test(x) && x!==options.product).slice(0,8) : [];
    if (!handles.length) { list.replaceChildren(); this.hidden=true; this.removeAttribute('aria-busy'); return; }
    this.setAttribute('aria-busy','true');
    let next=0, failed=false; const results=new Array(handles.length);
    await Promise.all(Array.from({length:Math.min(4,handles.length)},async()=> {
      while (next<handles.length && !signal.aborted) {
        const index=next++, handle=handles[index];
        try {
          const url=new URL(`${cfg.root.replace(/\/$/,'')}/products/${encodeURIComponent(handle)}`,location.origin);
          url.searchParams.set('section_id','yoc-wishlist-card');
          const response=await fetch(url,{signal,cache:'no-store'});
          if (response.status===404) continue;
          if (!response.ok) throw new Error('Request failed');
          const doc=new DOMParser().parseFromString(await response.text(),'text/html');
          const product=doc.querySelector('[data-wish-product]');
          if (product?.dataset.handle===handle) results[index]=product.querySelector('.yoc-card');
        } catch(error) { if (error.name!=='AbortError') failed=true; }
      }
    }));
    if (signal.aborted || !this.isConnected || !preferences()) return;
    list.replaceChildren(...results.filter(Boolean)); this.hidden=!list.children.length && !failed;
    if (status) status.textContent=failed ? strings.recentFailed : '';
    if (retry) retry.hidden=!failed;
    this.removeAttribute('aria-busy'); document.dispatchEvent(new CustomEvent('yoc:content'));
  }
  disconnectedCallback() { this.events?.abort(); this.request?.abort(); }
}

class YocBackTop extends HTMLElement {
  connectedCallback() {
    this.events=new AbortController();
    this.addEventListener('click',event=> {
      event.preventDefault(); window.scrollTo({top:0,behavior:scrollBehavior()});
      const main=document.getElementById('MainContent');
      if (main) { const had=main.hasAttribute('tabindex'); main.setAttribute('tabindex','-1'); main.focus({preventScroll:true}); if (!had) main.addEventListener('blur',()=>main.removeAttribute('tabindex'),{once:true}); }
    },{signal:this.events.signal});
  }
  disconnectedCallback() { this.events?.abort(); }
}

class YocExternal extends HTMLElement {
  connectedCallback() {
    this.events=new AbortController();
    this.querySelector('[data-external-load]')?.addEventListener('click',()=>this.load(),{signal:this.events.signal});
    document.addEventListener('visitorConsentCollected',()=> { if (!marketing()) this.unload(); },{signal:this.events.signal});
  }
  async load() {
    if (!marketing()) {
      if (window.privacyBanner?.showPreferences) { try { await window.privacyBanner.showPreferences(); } catch { /* Existing footer link remains available. */ } }
      else this.querySelector('[data-external-status]').textContent=cfg.strings?.consent || '';
      return;
    }
    let url;
    try { url=new URL(this.dataset.url); } catch { return; }
    const allowed=(url.hostname==='www.youtube-nocookie.com' && /^\/embed\/[\w-]{6,32}$/.test(url.pathname)) ||
      (url.hostname==='player.vimeo.com' && /^\/video\/\d+$/.test(url.pathname)) ||
      (url.hostname==='www.openstreetmap.org' && url.pathname==='/export/embed.html') ||
      (url.hostname==='www.google.com' && url.pathname.startsWith('/maps/embed'));
    if (url.protocol!=='https:' || !allowed || url.username || url.password || this.querySelector('iframe')) return;
    const iframe=document.createElement('iframe'); iframe.src=url.href; iframe.title=this.dataset.title || strings.external;
    iframe.setAttribute('sandbox','allow-scripts allow-same-origin allow-presentation'); iframe.allowFullscreen=true; iframe.referrerPolicy='no-referrer';
    this.querySelector('[data-external-placeholder]').hidden=true; this.append(iframe);
  }
  unload() { this.querySelector('iframe')?.remove(); this.querySelector('[data-external-placeholder]').hidden=false; }
  disconnectedCallback() { this.events?.abort(); this.querySelector('iframe')?.remove(); }
}

class YocGiftNote extends HTMLElement {
  connectedCallback() {
    this.events=new AbortController();
    this.querySelector('form')?.addEventListener('submit',event=>this.save(event),{signal:this.events.signal});
  }
  async save(event) {
    event.preventDefault(); if (this.pending) return; this.pending=true;
    const input=this.querySelector('textarea'), button=this.querySelector('button'), status=this.querySelector('[role=status]');
    button.disabled=true; input.readOnly=true; this.setAttribute('aria-busy','true'); status.textContent=''; status.removeAttribute('data-error');
    try {
      const response=await fetch(`${cfg.cart}/update.js`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({attributes:{'Geschenkhinweis':input.value.slice(0,500)}})});
      if (!response.ok) throw new Error('Request failed');
      const cart=await response.json();
      if (cart.attributes?.Geschenkhinweis!==input.value.slice(0,500) && input.value!=='') throw new Error('Not confirmed');
      status.textContent=strings.noteSaved;
      document.dispatchEvent(new CustomEvent('yoc:cart-changed',{detail:cart}));
    } catch { const icon=document.createElement('span'); icon.textContent='⚠'; icon.setAttribute('aria-hidden','true'); status.replaceChildren(icon,document.createTextNode(cfg.strings?.network || '')); status.setAttribute('data-error','true'); }
    finally { this.pending=false; button.disabled=false; input.readOnly=false; this.removeAttribute('aria-busy'); }
  }
  disconnectedCallback() { this.events?.abort(); }
}

function faqSchema() {
  const existing=document.getElementById('YocFaqSchema');
  if (!options.faqSchema) { existing?.remove(); return; }
  // One theme-owned FAQPage at most. Merchants can disable it when an app owns FAQ data.
  const seen=new Set(), entities=[];
  document.querySelectorAll('[data-yoc-faq-group] details').forEach(item=> {
    const question=item.querySelector('summary')?.textContent.trim(), answer=item.querySelector('.rte')?.textContent.trim();
    if (!question || !answer || seen.has(question)) return;
    seen.add(question); entities.push({'@type':'Question',name:question,acceptedAnswer:{'@type':'Answer',text:answer}});
  });
  if (!entities.length) { existing?.remove(); return; }
  const script=existing || document.createElement('script'); script.id='YocFaqSchema'; script.type='application/ld+json';
  script.textContent=JSON.stringify({'@context':'https://schema.org','@type':'FAQPage',mainEntity:entities}).replace(/</g,'\\u003c');
  if (!existing) document.body.append(script);
}

for (const [name, element] of Object.entries({
  'yoc-content-tabs':YocContentTabs,'yoc-article':YocArticle,'yoc-recent-searches':YocRecentSearches,
  'yoc-recently-viewed':YocRecentlyViewed,'yoc-back-top':YocBackTop,'yoc-external':YocExternal,'yoc-gift-note':YocGiftNote,
})) if (!customElements.get(name)) customElements.define(name,element);

document.addEventListener('visitorConsentCollected',reconcileConsent);
document.addEventListener('yoc:privacy-ready',reconcileConsent);
document.addEventListener('shopify:section:load',()=> { faqSchema(); });
document.addEventListener('yoc:content',faqSchema);
window.addEventListener('storage',event=> { if ([viewKey,searchKey].includes(event.key)) changed(); });
window.addEventListener('pageshow',event=> { if (event.persisted) reconcileConsent(); });
document.addEventListener('submit',event=> {
  if (!options.searches || !preferences() || !(event.target instanceof HTMLFormElement)) return;
  const form=event.target, query=form.querySelector('[name=q]'); if (!query) return;
  const url=new URL(form.action,location.href), expected=new URL(options.searchURL || `${cfg.root}search`,location.origin);
  if (url.origin===expected.origin && url.pathname===expected.pathname) { remember(searchKey,query.value.trim().slice(0,120)); changed(); }
},{capture:true});
reconcileConsent(); faqSchema();
